import { useState, useEffect } from "react";
import toast from "../../../../utils/toast";
import {
  getPackageList,
  togglePackageStatusById,
  deletePackagesById,
  getPackageById,
  updatePackageById,
  getPackageFeaturesList,
} from "../../../../services/admin/adminServices";
import { useNavigate } from "react-router-dom";

export default function PackageList() {
  const navigate = useNavigate();
  const [isPackageList, setPackageList] = useState([]);
  // const [isModalOpenPkg, setIsModalOpenPkg] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    packageId: null,
    packageName: "",
  });

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [masterFeatures, setMasterFeatures] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const INT_MAX = 2147483647;

  useEffect(() => {
    fetchPackagesList();
  }, []);

  useEffect(() => {
    if (editModal) {
      fetchFeatures();
    }
  }, [editModal]);

  const fetchPackagesList = async () => {
    setListLoading(true);
    try {
      const res = await getPackageList();
      if (res.statusCode === 1) {
        setPackageList(res?.data);
      }
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch packages");
    } finally {
      setListLoading(false);
    }
  };

  const openDeleteModal = (pkg) => {
    setDeleteModal({
      open: true,
      packageId: pkg.packageId,
      packageName: pkg.name,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, packageId: null, packageName: "" });
  };

  const handleDeletePackage = async () => {
    try {
      const res = await deletePackagesById(deleteModal.packageId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeDeleteModal();
        fetchPackagesList();
      } else {
        toast.error(res.message );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete package.");
    }
  };
 
  const handleToggleStatus = async (packageId, status) => {
    try {
      const res = await togglePackageStatusById(packageId, !status);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchPackagesList();
      }
    } catch (err) {
      toast.error("Failed to toggle user status.");
    }
  };

  const COLORS = [
    "var(--blue)",
    "var(--teal)",
    "#7c3aed",
    "var(--amber)",
    "var(--green)",
  ];

  const fetchFeatures = async () => {
    try {
      const res = await getPackageFeaturesList();
      setMasterFeatures(res || []);
    } catch (err) {
      console.log("Failed to fetch features");
    }
  };

  const openEditPackage = async (pkg) => {
    try {
      const res = await getPackageById(pkg.packageId);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedPkg(d);
        setEditForm({
          name: d.name || "",
          tagline: d.tagline || "",
          priceMonthly: d.priceMonthly || "",
          priceYearly: d.priceYearly || "",
          validityDays: d.validityDays || 30,
          sortOrder: d.sortOrder || 1,
          maxProperties: d.maxProperties === -1 ? "" : d.maxProperties || "",
          maxPropertiesUnlimited: d.maxProperties === -1,
          maxTenants: d.maxTenants === -1 ? "" : d.maxTenants || "",
          maxTenantsUnlimited: d.maxTenants === -1,
          maxCollections: d.maxCollections === -1 ? "" : d.maxCollections || "",
          maxCollectionsUnlimited: d.maxCollections === -1,
          maxRooms: d.maxRooms === -1 ? "" : d.maxRooms || "",
          maxRoomsUnlimited: d.maxRooms === -1,
          smsLimit: d.smsLimit || "",
          smsAfterPrice: d.smsAfterPrice || "",
          waLimit: d.waLimit || "",
          waAfterPrice: d.waAfterPrice || "",
          emailLimit: d.emailLimit || "",
          emailAfterPrice: d.emailAfterPrice || "",
          storageGB: d.storageGB === INT_MAX ? "" : d.storageGB || "",
          storageUnlimited: d.storageGB === INT_MAX,
          enablePG: d.enablePG || false,
          enablePM: d.enablePM || false,
          isActive: d.isActive,
          features: d.features?.map((f) => f.masterFeatureId) || [],
        });
        setEditErrors({});
        setEditModal(true);
      }
    } catch (err) {
      toast.error("Failed to load package details.");
    }
  };

  const closeEditModal = () => {
    setEditModal(false);
    setSelectedPkg(null);
    setEditForm({});
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditFeatureToggle = (id) => {
    setEditForm((prev) => ({
      ...prev,
      features: prev.features.includes(id)
        ? prev.features.filter((f) => f !== id)
        : [...prev.features, id],
    }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.name) e.name = "Package name is required.";
    if (!editForm.priceMonthly) e.priceMonthly = "Monthly price is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdatePackage = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        tagline: editForm.tagline,
        priceMonthly: Number(editForm.priceMonthly),
        priceYearly: Number(editForm.priceYearly) || 0,
        validityDays: Number(editForm.validityDays) || INT_MAX,
        sortOrder: Number(editForm.sortOrder) || 0,
        maxProperties: editForm.maxPropertiesUnlimited
          ? -1
          : Number(editForm.maxProperties) || 0,
        maxTenants: editForm.maxTenantsUnlimited
          ? -1
          : Number(editForm.maxTenants) || 0,
        maxCollections: editForm.maxCollectionsUnlimited
          ? -1
          : Number(editForm.maxCollections) || 0,
        maxRooms: editForm.maxRoomsUnlimited
          ? -1
          : Number(editForm.maxRooms) || 0,
        smsLimit: Number(editForm.smsLimit) || 0,
        smsAfterPrice: Number(editForm.smsAfterPrice) || 0,
        waLimit: Number(editForm.waLimit) || 0,
        waAfterPrice: Number(editForm.waAfterPrice) || 0,
        emailLimit: Number(editForm.emailLimit) || 0,
        emailAfterPrice: Number(editForm.emailAfterPrice) || 0,
        storageGB: editForm.storageUnlimited
          ? INT_MAX
          : Number(editForm.storageGB) || 0,
        enablePG: editForm.enablePG,
        enablePM: editForm.enablePM,
        isActive: editForm.isActive === "true" || editForm.isActive === true,
        features: editForm.features,
      };

      const res = await updatePackageById(selectedPkg.packageId, payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Package updated successfully.");
        closeEditModal();
        fetchPackagesList();
      } else {
        toast.error(res.message || "Failed to update package.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Package List
            </div>
            <div className="page-sub" id="page-sub">
              Packages › All Plans
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/admin/create-package")}
            >
              + Create Package
            </button>
          </div>
        </div>
        <div className="content" id="content-area">
          <div className="card mb16">
            <div className="flex-between mb12">
              <div className="card-title" style={{ margin: 0 }}>
                All Subscription Packages
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>S.N.</th>
                    <th>Package Name</th>
                    <th>Monthly</th>
                    <th>Yearly</th>
                    <th>Properties</th>
                    <th>Tenants</th>
                    <th>Rooms</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {listLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width:
                                  j === 1 ? "80%" : j === 8 ? "60%" : "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : isPackageList?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No packages found.
                      </td>
                    </tr>
                  ) : (
                    isPackageList?.map((pkg, index) => (
                      <tr key={pkg.packageId}>
                        <td className="sn-col">{index + 1}</td>
                        <td>
                          <strong>{pkg.name}</strong>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            [{pkg.packageId || ""}]
                          </div>
                        </td>
                        <td>₹{pkg.priceMonthly?.toLocaleString("en-IN")}/mo</td>
                        <td>₹{pkg.priceYearly?.toLocaleString("en-IN")}/yr</td>
                        <td>{pkg.maxProperties}</td>
                        <td>{pkg.maxTenants}</td>
                        <td>{pkg.maxRooms}</td>

                        <td>
                          <span
                            className={`badge ${pkg.isActive ? "badge-active" : "badge-inactive"}`}
                          >
                            {pkg.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              onClick={() => openEditPackage(pkg)}
                              title="Edit"
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                            <span
                              className="action-icon amber"
                              title="Change Status"
                              onClick={() =>
                                handleToggleStatus(
                                  pkg?.packageId,
                                  pkg?.isActive,
                                )
                              }
                            >
                              <span className="tip">Toggle Status</span>⇄
                            </span>
                            <span
                              className="action-icon red"
                              onClick={() => openDeleteModal(pkg)}
                              title="Delete"
                            >
                              <span className="tip">Delete</span>🗑
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            className="cus-grid-054"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,minmax(0,1fr))",
              gap: 14,
            }}
          >
            {isPackageList?.map((pkg, i) => {
              const color = COLORS[i % COLORS.length];
              const savePercent =
                pkg.priceMonthly && pkg.priceYearly
                  ? Math.round(
                      (1 - pkg.priceYearly / (pkg.priceMonthly * 12)) * 100,
                    )
                  : 0;

              return (
                <div
                  key={pkg.packageId}
                  className="card"
                  style={{
                    borderTop: `3px solid ${color}`,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color,
                      marginBottom: 2,
                    }}
                  >
                    {pkg.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginBottom: 10,
                    }}
                  >
                    {pkg.tagline}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color }}>
                    ₹{pkg.priceMonthly?.toLocaleString("en-IN")}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        color: "var(--text-secondary)",
                      }}
                    >
                      /mo
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--text-tertiary)",
                      marginBottom: 12,
                    }}
                  >
                    ₹{pkg.priceYearly?.toLocaleString("en-IN")}/year
                    {savePercent > 0 && ` (save ${savePercent}%)`}
                  </div>

                  <ul className="pkg-features">
                    <li>Up to {pkg.maxProperties} Properties</li>

                    <li>Up to {pkg.maxTenants} Tenants</li>

                    <li>Up to {pkg.maxCollections} Collections</li>

                    <li>Up to {pkg.maxRooms} Rooms</li>

                    <li>{pkg.smsLimit} SMS / month</li>
                    <li>{pkg.waLimit} WhatsApp msgs / month</li>
                    <li> {pkg.storageGB} GB Storage </li>
                    {pkg.features?.map((f) => (
                      <li key={f.masterFeatureId}>{f.featureName}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 720 }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit Package — {selectedPkg?.name}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Basic Info */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Package Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    placeholder="e.g. Starter, Growth, Pro"
                    style={editErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Tagline</label>
                  <input
                    type="text"
                    name="tagline"
                    value={editForm.tagline}
                    onChange={handleEditChange}
                    placeholder="Perfect for small properties"
                  />
                </div>
                <div className="form-group">
                  <label>Monthly Price (₹) *</label>
                  <input
                    type="number"
                    name="priceMonthly"
                    value={editForm.priceMonthly}
                    onChange={handleEditChange}
                    placeholder="999"
                    style={
                      editErrors.priceMonthly ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {editErrors.priceMonthly && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.priceMonthly}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Yearly Price (₹)</label>
                  <input
                    type="number"
                    name="priceYearly"
                    value={editForm.priceYearly}
                    onChange={handleEditChange}
                    placeholder="9999"
                  />
                </div>
                <div className="form-group">
                  <label>Validity (Days)</label>
                  <input
                    type="number"
                    name="validityDays"
                    value={editForm.validityDays}
                    onChange={handleEditChange}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={editForm.sortOrder}
                    onChange={handleEditChange}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="isActive"
                    value={editForm.isActive}
                    onChange={handleEditChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
                <div className="form-group" />
                <div className="form-group">
                  <label>Enable Payment Gateway</label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: 13,
                      fontWeight: 400,
                      marginTop: 6,
                    }}
                  >
                    <input
                      type="checkbox"
                      name="enablePG"
                      checked={editForm.enablePG}
                      onChange={handleEditChange}
                    />
                    Enable PG
                  </label>
                </div>
                <div className="form-group">
                  <label>Enable Payment Management</label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: 13,
                      fontWeight: 400,
                      marginTop: 6,
                    }}
                  >
                    <input
                      type="checkbox"
                      name="enablePM"
                      checked={editForm.enablePM}
                      onChange={handleEditChange}
                    />
                    Enable PM
                  </label>
                </div>
              </div>

              <hr className="section-divider" />
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
                Property & Usage Limits
              </div>

              <div className="form-grid">
                {/* Unlimited fields */}
                {[
                  {
                    label: "Allowed Properties",
                    name: "maxProperties",
                    unlimitedKey: "maxPropertiesUnlimited",
                  },
                  {
                    label: "Max Tenants",
                    name: "maxTenants",
                    unlimitedKey: "maxTenantsUnlimited",
                  },
                  {
                    label: "Rent Collections",
                    name: "maxCollections",
                    unlimitedKey: "maxCollectionsUnlimited",
                  },
                  {
                    label: "Max Rooms",
                    name: "maxRooms",
                    unlimitedKey: "maxRoomsUnlimited",
                  },
                ].map(({ label, name, unlimitedKey }) => (
                  <div className="form-group" key={name}>
                    <label>{label}</label>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <input
                        type="number"
                        name={name}
                        value={editForm[name]}
                        onChange={handleEditChange}
                        disabled={editForm[unlimitedKey]}
                        placeholder="e.g. 5"
                        style={{
                          flex: 1,
                          opacity: editForm[unlimitedKey] ? 0.4 : 1,
                        }}
                      />
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                          display: "flex",
                          gap: 4,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          name={unlimitedKey}
                          checked={editForm[unlimitedKey]}
                          onChange={handleEditChange}
                        />
                        Unlimited
                      </label>
                    </div>
                  </div>
                ))}

                <div className="form-group">
                  <label>SMS Limit / month</label>
                  <input
                    type="number"
                    name="smsLimit"
                    value={editForm.smsLimit}
                    onChange={handleEditChange}
                    placeholder="1000"
                  />
                </div>
                <div className="form-group">
                  <label>SMS After Limit Price (₹)</label>
                  <input
                    type="number"
                    name="smsAfterPrice"
                    value={editForm.smsAfterPrice}
                    onChange={handleEditChange}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp Limit / month</label>
                  <input
                    type="number"
                    name="waLimit"
                    value={editForm.waLimit}
                    onChange={handleEditChange}
                    placeholder="2000"
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp After Limit Price (₹)</label>
                  <input
                    type="number"
                    name="waAfterPrice"
                    value={editForm.waAfterPrice}
                    onChange={handleEditChange}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Email Limit / month</label>
                  <input
                    type="number"
                    name="emailLimit"
                    value={editForm.emailLimit}
                    onChange={handleEditChange}
                    placeholder="500"
                  />
                </div>
                <div className="form-group">
                  <label>Email After Limit Price (₹)</label>
                  <input
                    type="number"
                    name="emailAfterPrice"
                    value={editForm.emailAfterPrice}
                    onChange={handleEditChange}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Storage (GB)</label>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <input
                      type="number"
                      name="storageGB"
                      value={editForm.storageGB}
                      onChange={handleEditChange}
                      disabled={editForm.storageUnlimited}
                      placeholder="10"
                      style={{
                        flex: 1,
                        opacity: editForm.storageUnlimited ? 0.4 : 1,
                      }}
                    />
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        display: "flex",
                        gap: 4,
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        name="storageUnlimited"
                        checked={editForm.storageUnlimited}
                        onChange={handleEditChange}
                      />
                      Unlimited
                    </label>
                  </div>
                </div>
              </div>

              <hr className="section-divider" />
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
                Included Features
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                {masterFeatures.map((f) => (
                  <label
                    key={f.masterFeatureId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: 12,
                      fontWeight: 400,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={editForm.features?.includes(f.masterFeatureId)}
                      onChange={() =>
                        handleEditFeatureToggle(f.masterFeatureId)
                      }
                    />
                    {f.featureName}
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdatePackage}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.open && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Delete Package</div>
              <button className="modal-close" onClick={closeDeleteModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>🗑</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
                  Delete "{deleteModal.packageName}"?
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-secondary)",
                    lineHeight: 1.6,
                  }}
                >
                  This action is permanent and cannot be undone.
                </div>
              </div>
            </div>
            <div
              className="modal-footer"
              style={{ justifyContent: "center", gap: 12 }}
            >
              <button
                className="btn"
                onClick={closeDeleteModal}
                style={{ minWidth: 100 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeletePackage}
                style={{
                  minWidth: 100,
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
